import Link from 'next/link'
import { useRouter } from 'next/router'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useEffectOnce } from 'react-use'

import Submit from '@/components/buttons/Primary'
import LoadingIcon from '@/components/icons/Loading'
import Layout from '@/components/Layout'
import { useAuthentication } from '@/components/providers/AuthenticationProvider'
import { useDependencies } from '@/components/providers/DependencyProvider'
import { Status, useStatus } from '@/hooks/useStatus'

interface AccountStatus {
	approved: boolean,
	verified: boolean,
}

function VerifyAccount(): JSX.Element {
	const [status, setStatus] = useStatus(Status.Idle)
	const { apiFetcher } = useDependencies()
	const handler = useCallback(() => {
		if (status === Status.Idle || status === Status.Error) {
			setStatus(Status.Loading)

			apiFetcher('/resend-verification-email', {
				method: 'POST',
			}).then(() => {
				setStatus(Status.Success)
			}).catch(() => {
				setStatus(Status.Error)
			})
		}
	}, [apiFetcher, setStatus])

	return (
		<div className="container mx-auto text-center py-16 md:py-32">
			<h2 className="text-2xl font-semibold">Awaiting Email Verification.</h2>
			<p>Your email has not yet been verified. Please click the link in your email to verify it.</p>

			<Submit onClick={handler} type="button" className="px-4 mt-4">Resend verification email</Submit>

			{ status === Status.Error && (
				<p className="mt-4 font-bold text-red-700">Unexpected error. Try again later</p>
			)}

			{ status === Status.Success && (
				<p className="mt-4 font-bold text-green-400">Email Sent Successfully</p>
			)}
		</div>
	)
}

export default function AccountLayout({ children }: { children: ReactNode }): JSX.Element {
	const { apiFetcher } = useDependencies()
	const { email } = useAuthentication()
	const router = useRouter()
	const [status, setStatus] = useState<AccountStatus | undefined>(undefined)

	useEffectOnce(() => {
		let cancelled = false

		apiFetcher<AccountStatus>('/account-status').then(status => {
			if (!cancelled) {
				setStatus(status)
			}
		}).catch(() => void router.replace(`/login?back=${router.asPath}`))

		return () => {
			cancelled = true
		}
	})

	useEffect(() => {
		let cancelled = false

		if (email === null) {
			setTimeout(() => {
				if (!cancelled) {
					void router.replace(`/login?back=${router.asPath}`)
				}
			}, 150)
		}

		return () => {
			cancelled = true
		}
	}, [email, router.asPath])

	if (typeof email !== 'string' || status === undefined) {
		return (
			<Layout>
				<div className="py-32 flex justify-center items-center">
					<LoadingIcon />
				</div>
			</Layout>
		)
	}

	if (!status.verified) {
		return (
			<Layout>
				<VerifyAccount />
			</Layout>
		)
	}

	if (!status.approved) {
		return (
			<Layout>
				<div className="container mx-auto text-center px-3 py-16 md:py-32">
					<h2 className="text-2xl font-semibold">Thank you for signing up.</h2>
					<p>Your account is now pending approval. You may still place orders while we review your information.</p>
					<p className="mt-6 text-sm">If you believe you are seeing this message in error, please <Link href="/contact-us"><a className="font-extrabold text-green-400">contact us.</a></Link></p>
				</div>
			</Layout>
		)
	}

	return (
		<Layout>
			{children}
		</Layout>
	)
}
